import cx from "classnames";

import RecordFieldsTable from "../../Composites/RecordFieldsTable/RecordFieldsTable";
import { appstr } from "@coreapp/utils/appstr";
import { Button } from "@theorgchartdev/storybook";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import type { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { EmployeeHeadingProfileCard } from "./Headers/EmployeeHeadingProfileCard";
import { getTabFieldData, ProfileTypes } from "./ProfileBaseballCard.utils";
import { getDisplayMap } from "./getDisplayMap";
import { getMainDisplayFieldIds } from "@/Utils/getMainDisplayFieldIds";
import { BaseballCardParentReference } from "./BaseballCardParentReference";
import { useRecordPhotoSrc } from "@/hooks/useRecordPhotoSrc";
import {
  HeadingConfigType,
  getDefaultEmployeeFieldConfig
} from "../../Modals/SettingsModal/ChartSettings/ProfileConfiguration/ProfileConfiguration.utils";

export interface FavoriteManagerInterface {
  isStarringEnabled: boolean;
  getCurrentFavorites: () => string[];
  toggleStarredField: (field: string) => void;
  removeStarredField: (field: { field: string; value: string }) => void;
}

export type EmployeeCardProps = {
  record: Record<string, string>;
  headingConfig: HeadingConfigType;
  isPositionChart: boolean;
  favoriteController?: FavoriteManagerInterface;
  visibleFields?: string[];

  includeScrollableArea?: boolean;

  doc?: QXDoc;
  dataObj?: Nullish<QObjDBObject>;
  mapping: Record<string, string>;

  showMoreFields: boolean | null;
  setShowMoreFields: React.Dispatch<React.SetStateAction<boolean | null>>;
};

export const EmployeeBaseballCard = (props: EmployeeCardProps) => {
  const baseClass = "ocn-profile-card";

  const shouldShowMoreFields = props.showMoreFields ?? true;
  const { dept: deptFieldId, ...mainFieldIds } = getMainDisplayFieldIds(
    props.isPositionChart,
    "Holder"
  );

  const upperDisplayMap: Record<string, string> = !props.doc
    ? {
        name: props.mapping[mainFieldIds.name] || mainFieldIds.name,
        id: props.mapping[mainFieldIds.id] || mainFieldIds.id,
        title: props.mapping[mainFieldIds.title] || mainFieldIds.title,
        photo: props.mapping[mainFieldIds.photo] || mainFieldIds.photo
      }
    : getDisplayMap(mainFieldIds, "Box", false);
  const parentReference = props.dataObj?.GetParentPosition();

  // Division is optional and only set it when actually mapped, no fallback to the literal field ID
  if (props.mapping[deptFieldId]) {
    upperDisplayMap.dept = props.mapping[deptFieldId];
  }

  const photoSrc = useRecordPhotoSrc(
    upperDisplayMap.photo ? props.record?.[upperDisplayMap.photo] : undefined
  );

  if (!props.visibleFields) {
    return null;
  }

  const deptText = upperDisplayMap.dept ? props.record[upperDisplayMap.dept] : undefined;
  const basicFieldData = {
    header1Text: props.record[upperDisplayMap.name],
    showHeader1Text: upperDisplayMap.hasOwnProperty("name"),
    subheader1Text: props.record[upperDisplayMap.id],
    showSubheader1: upperDisplayMap.hasOwnProperty("id"),
    header2Text: props.record[upperDisplayMap.title],
    showHeader2: upperDisplayMap.hasOwnProperty("title"),
    header3Text: deptText,
    showHeader3: !!deptText,
    photoSrc
  };
  const favorites = props.favoriteController?.getCurrentFavorites() || [];

  // Exclude fields already shown in the heading
  const showMoreFields = props.visibleFields.filter(fieldId => {
    return !Object.values(upperDisplayMap).includes(fieldId) && !favorites.includes(fieldId);
  });
  const tabFieldData = getTabFieldData(showMoreFields, props.record, ProfileTypes.Employee);

  return (
    <>
      {parentReference && props.doc && (
        <BaseballCardParentReference
          doc={props.doc}
          dataObj={parentReference}
          setShowMoreFields={props.setShowMoreFields}
        />
      )}
      <EmployeeHeadingProfileCard
        config={
          props.headingConfig?.[ProfileTypes.Employee] ??
          getDefaultEmployeeFieldConfig(props.isPositionChart, props.mapping)
        }
        favoriteController={props.favoriteController}
        showAssignmentStatus={props.isPositionChart}
        record={props.record}
        {...basicFieldData}
      />
      {tabFieldData.length !== 0 && (
        <div
          className={cx({
            [`${baseClass}-content`]: props.includeScrollableArea,
            "ocn-list-scrollbar": props.includeScrollableArea,
            [`${baseClass}-more-fields-container`]: true
          })}
        >
          {shouldShowMoreFields && (
            <>
              <RecordFieldsTable
                className={baseClass}
                data={tabFieldData}
                sortEnabled={false}
                enableStarring={props.favoriteController?.isStarringEnabled}
                starredFields={favorites}
                toggleFavoriteField={props.favoriteController?.toggleStarredField}
              />
            </>
          )}
          <div className={`${baseClass}-additional`}>
            <Button
              dataTestId="profile-card-more-toggle"
              onClick={() => props.setShowMoreFields(!shouldShowMoreFields)}
              size={"small"}
              modifier="tertiary"
              iconProps={{
                name: shouldShowMoreFields ? "square-minus" : "square-plus",
                size: "small"
              }}
            >
              {shouldShowMoreFields ? appstr("Show less") : appstr("Show more")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
